export interface ObjectWithId {
  id: string;
}
export type ObjectsWithId = ObjectWithId[];

const safeBtoa = (str: string) => btoa(encodeURIComponent(str));

export function createObjectOperations<MainT extends ObjectWithId>(_nodes: MainT[]) {
  const nodes = [..._nodes];
  const nodesIndexMap = new Map<string, MainT>();
  nodes.forEach((node) => {
    nodesIndexMap.set(safeBtoa(node.id), node);
  });

  function createObjectGetter() {
    return function <T extends MainT>(id: string) {
      return nodesIndexMap.get(safeBtoa(id)) as T | undefined;
    };
  }

  function createObjectSetter() {
    return function <T extends MainT>(id: string, nodeOrUpdater: T | ((node: T) => T)) {
      const currentNode = nodesIndexMap.get(safeBtoa(id));

      if (currentNode === undefined) return;

      if (typeof nodeOrUpdater === 'function') {
        nodesIndexMap.set(safeBtoa(id), nodeOrUpdater(currentNode as T));
      } else {
        nodesIndexMap.set(safeBtoa(id), nodeOrUpdater as MainT);
      }
    };
  }

  function createObjectRemover() {
    return function (id: string) {
      nodesIndexMap.delete(safeBtoa(id));
    };
  }

  function createObjectAdder() {
    return function <T extends MainT>(node: T) {
      const _safeId = safeBtoa(node.id);
      if (nodesIndexMap.has(_safeId)) {
        throw new Error(`Node with id ${node.id} already exists`);
      }
      nodesIndexMap.set(_safeId, node);
    };
  }

  function createObjectUpsert() {
    return function <T extends MainT>(node: T) {
      const _safeId = safeBtoa(node.id);
      nodesIndexMap.set(_safeId, { ...node });
    };
  }

  /**
   * Set objects by selector
   * @param selector - A function that returns true if the node should be updated
   * @param nodeOrUpdater - A node or a function that returns a node
   */
  function createObjectSetterBySelector() {
    return function <T extends MainT>(
      selector: (node: T) => boolean,
      nodeOrUpdater: T | ((node: T) => T),
    ) {
      nodesIndexMap.forEach((node, key) => {
        if (selector(node as T)) {
          if (typeof nodeOrUpdater === 'function') {
            nodesIndexMap.set(key, nodeOrUpdater(node as T));
          } else {
            nodesIndexMap.set(key, nodeOrUpdater as MainT);
          }
        }
      });
    };
  }

  /**
   * Affect all objects with a partial update
   * @param partialNodeOrUpdater - A partial node or a function that returns a complete node
   */
  function crateAffectAllObjectsWithPartial() {
    return function <T extends MainT>(partialNodeOrUpdater: Partial<T> | ((node: T) => T)) {
      nodesIndexMap.forEach((node) => {
        if (typeof partialNodeOrUpdater === 'function') {
          nodesIndexMap.set(safeBtoa(node.id), partialNodeOrUpdater(node as T));
        } else {
          nodesIndexMap.set(safeBtoa(node.id), {
            ...node,
            ...partialNodeOrUpdater,
          } as unknown as MainT);
        }
      });
    };
  }

  function createGetObjectsBySelector() {
    return function <T extends MainT>(selector: (node: T) => boolean) {
      return Array.from(nodesIndexMap.values()).filter((node) => selector(node as T)) as T[];
    };
  }

  function createFindFirstObjectBySelector() {
    return function <T extends MainT>(selector: (node: T) => boolean) {
      return Array.from(nodesIndexMap.values()).find((node) => selector(node as T)) as
        | T
        | undefined;
    };
  }

  /**
   * Set all  objects
   * @param nodes - An array of nodes
   */
  function createSetObjects() {
    return function <T extends MainT>(nodes: T[]) {
      nodes.forEach((node) => {
        nodesIndexMap.set(safeBtoa(node.id), node);
      });
    };
  }

  return {
    getObject: createObjectGetter(),
    setObject: createObjectSetter(),
    setObjectsBySelector: createObjectSetterBySelector(),
    findObjectsBySelector: createGetObjectsBySelector(),
    findFirstObjectBySelector: createFindFirstObjectBySelector(),
    affectAllObjectsWithPartial: crateAffectAllObjectsWithPartial(),
    removeObject: createObjectRemover(),
    addObject: createObjectAdder(),
    upsertObject: createObjectUpsert(),
    hasObject: (nodeId: string) => nodesIndexMap.has(safeBtoa(nodeId)),
    getObjects: () => Array.from(nodesIndexMap.values()),
    setObjects: createSetObjects(),
  };
}

export type ObjectOperations<MainT extends ObjectWithId> = ReturnType<
  typeof createObjectOperations<MainT>
>;
